/**
 * PentestWorkflowSelector — Predefined pentest workflow templates
 * Inspired by pcybox-attack: 5 workflows (Web, Network, API, Cloud, AD)
 */
import React, { useState, useEffect } from 'react';
import {
  Globe, Network, Zap, Cloud, Building2, Play, ChevronRight,
  Loader2, CheckCircle, Clock, Wrench, AlertTriangle, X, Target,
  ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from '../utils/toastUtils';

const API_BASE = '/api/v1/workflows';

const CATEGORY_ICONS = {
  web: <Globe className="w-5 h-5" />,
  network: <Network className="w-5 h-5" />,
  cloud: <Cloud className="w-5 h-5" />,
  internal: <Building2 className="w-5 h-5" />,
};

const DIFFICULTY_COLORS = {
  beginner: 'green',
  intermediate: 'yellow',
  advanced: 'orange',
  expert: 'red',
};

const DIFFICULTY_LABELS = {
  beginner: 'Beginner',
  intermediate: 'Intermediate',
  advanced: 'Advanced',
  expert: 'Expert',
};

const WorkflowCard = ({ workflow, onSelect, selected }) => {
  const [expanded, setExpanded] = useState(false);
  const diffColor = DIFFICULTY_COLORS[workflow.difficulty] || 'gray';
  const diffLabel = DIFFICULTY_LABELS[workflow.difficulty] || workflow.difficulty;

  return (
    <div
      className={`border rounded-xl overflow-hidden transition-all cursor-pointer ${
        selected
          ? 'border-brand-green/60 bg-brand-green/5'
          : 'border-gray-700/50 bg-brand-black-light hover:border-gray-600'
      }`}
      onClick={() => onSelect(workflow)}
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-xl ${
            selected ? 'bg-brand-green/20' : 'bg-gray-800'
          }`}>
            {workflow.icon}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="text-sm font-semibold text-white">{workflow.name}</h4>
              {selected && <CheckCircle className="w-4 h-4 text-brand-green flex-shrink-0" />}
            </div>
            <p className="text-xs text-gray-400 mt-0.5 line-clamp-2">{workflow.description}</p>
          </div>
        </div>

        {/* Meta */}
        <div className="flex items-center gap-3 mt-3 flex-wrap">
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3 text-gray-500" />
            <span className="text-xs text-gray-500">{workflow.estimated_duration}</span>
          </div>
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium bg-${diffColor}-900/20 text-${diffColor}-400 border border-${diffColor}-500/30`}>
            {diffLabel}
          </span>
          <span className="text-xs text-gray-600">{workflow.phases_count} phases</span>
        </div>

        {/* Tools */}
        <div className="flex flex-wrap gap-1 mt-2">
          {(workflow.tools_used || []).slice(0, 5).map((tool) => (
            <span key={tool} className="px-1.5 py-0.5 bg-gray-800 rounded text-[10px] text-gray-400 font-mono">{tool}</span>
          ))}
          {workflow.tools_used?.length > 5 && (
            <span className="px-1.5 py-0.5 bg-gray-800 rounded text-[10px] text-gray-500">+{workflow.tools_used.length - 5}</span>
          )}
        </div>

        {/* Expand phases */}
        <button
          onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
          className="mt-3 text-xs text-gray-500 hover:text-gray-300 flex items-center gap-1 transition-colors"
        >
          {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          Voir les phases
        </button>
      </div>

      {/* Phases detail */}
      {expanded && workflow.phases && (
        <div className="border-t border-gray-800 divide-y divide-gray-800">
          {workflow.phases.map((phase) => (
            <div key={phase.order} className="px-4 py-2.5 flex items-center gap-3">
              <span className="w-5 h-5 rounded-full bg-brand-green/20 border border-brand-green/40 text-brand-green text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                {phase.order}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-white font-medium">{phase.name}</p>
                <p className="text-xs text-gray-500 truncate">{phase.description}</p>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                {(phase.tools || []).slice(0, 2).map((t) => (
                  <span key={t} className="px-1 py-0.5 bg-gray-800 rounded text-[9px] text-gray-500 font-mono">{t}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================
// EXECUTION RESULT PANEL
// ============================================================
const ExecutionResult = ({ result, onClose }) => (
  <div className="bg-green-900/20 border border-green-500/30 rounded-xl p-5">
    <div className="flex items-start justify-between mb-4">
      <div className="flex items-center gap-3">
        <CheckCircle className="w-6 h-6 text-green-400" />
        <div>
          <p className="text-white font-semibold">{result.workflow_name} — Planned</p>
          <p className="text-xs text-gray-400 mt-0.5">
            {result.phases_count} phase(s) · Cible: <span className="text-white font-mono">{result.target}</span>
          </p>
        </div>
      </div>
      <button onClick={onClose} className="p-1 hover:bg-gray-800 rounded transition-colors">
        <X className="w-4 h-4 text-gray-400" />
      </button>
    </div>

    <p className="text-sm text-gray-300 mb-4">{result.message}</p>

    {/* Phases */}
    <div className="space-y-2 mb-4">
      {result.phases_planned?.map((phase) => (
        <div key={phase.order} className="flex items-center gap-3 p-2.5 bg-brand-black rounded-lg">
          <span className="w-5 h-5 rounded-full bg-brand-green/20 border border-brand-green/40 text-brand-green text-[10px] font-bold flex items-center justify-center flex-shrink-0">
            {phase.order}
          </span>
          <div className="flex-1">
            <p className="text-xs text-white font-medium">{phase.name}</p>
            <p className="text-xs text-gray-500">{phase.description}</p>
          </div>
          <span className="text-[10px] text-gray-600 px-1.5 py-0.5 bg-gray-800 rounded">{phase.status}</span>
        </div>
      ))}
    </div>

    {/* Next steps */}
    {result.next_steps?.length > 0 && (
      <div className="bg-brand-black rounded-lg p-3 border border-gray-800">
        <p className="text-xs text-gray-400 mb-2 font-medium">Next steps — AI commands by phase:</p>
        <div className="space-y-1.5">
          {result.next_steps.slice(0, 3).map((step, i) => (
            <div key={i} className="text-xs text-gray-500 flex items-center gap-2">
              <ChevronRight className="w-3 h-3 text-brand-green flex-shrink-0" />
              <span className="text-gray-400 font-medium">{step.phase}:</span>
              <span>Generate commands from <span className="font-mono text-brand-green">AI Command Generator</span></span>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

// ============================================================
// MAIN COMPONENT
// ============================================================
const PentestWorkflowSelector = ({ workspaceId = null, onWorkflowExecuted = null }) => {
  const [workflows, setWorkflows] = useState([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [selectedWorkflowDetail, setSelectedWorkflowDetail] = useState(null);
  const [target, setTarget] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingWorkflows, setLoadingWorkflows] = useState(true);
  const [executionResult, setExecutionResult] = useState(null);

  const authHeaders = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('access_token')}`
  };

  useEffect(() => {
    fetchWorkflows();
  }, []);

  const fetchWorkflows = async () => {
    try {
      const res = await fetch(API_BASE, { headers: authHeaders });
      const data = await res.json();
      setWorkflows(data.workflows || []);
    } catch (e) {
      toast.error('Impossible de charger les workflows');
    } finally {
      setLoadingWorkflows(false);
    }
  };

  const handleSelectWorkflow = async (wf) => {
    setSelectedWorkflow(wf.id);
    setExecutionResult(null);
    // Load full detail with phases
    try {
      const res = await fetch(`${API_BASE}/${wf.id}`, { headers: authHeaders });
      const detail = await res.json();
      setSelectedWorkflowDetail(detail);
    } catch {
      setSelectedWorkflowDetail(wf);
    }
  };

  const executeWorkflow = async () => {
    if (!selectedWorkflow) { toast.error('Select a workflow'); return; }
    if (!target.trim()) { toast.error('Entrez une cible'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/${selectedWorkflow}/execute`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          target: target.trim(),
          workspace_id: workspaceId || null,
        })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Erreur ${res.status}`);
      }
      const data = await res.json();
      setExecutionResult(data);
      toast.success(`Workflow "${data.workflow_name}" planned on ${target}`);
      if (onWorkflowExecuted) onWorkflowExecuted(data);
    } catch (e) {
      toast.error(`Erreur: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-brand-black-light border border-gray-700/50 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-800 bg-gradient-to-r from-indigo-900/20 to-purple-900/20">
        <div className="flex items-center gap-2 mb-1">
          <Wrench className="w-5 h-5 text-indigo-400" />
          <h3 className="text-base font-semibold text-white">Workflows Pentest</h3>
          <span className="ml-auto text-xs text-gray-500">{workflows.length} templates disponibles</span>
        </div>
        <p className="text-xs text-gray-500">Predefined templates inspired by pcybox-attack — select a workflow and a target</p>
      </div>

      <div className="p-4 space-y-4">
        {/* Workflow grid */}
        {loadingWorkflows ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-brand-green" />
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3">
            {workflows.map((wf) => (
              <WorkflowCard
                key={wf.id}
                workflow={selectedWorkflowDetail && selectedWorkflowDetail.id === wf.id
                  ? selectedWorkflowDetail
                  : wf}
                selected={selectedWorkflow === wf.id}
                onSelect={handleSelectWorkflow}
              />
            ))}
          </div>
        )}

        {/* Target + Execute */}
        {selectedWorkflow && !executionResult && (
          <div className="bg-brand-black rounded-xl border border-brand-green/20 p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-brand-green" />
              <span className="text-sm font-medium text-white">Configure execution</span>
            </div>
            <input
              type="text"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && executeWorkflow()}
              placeholder="Cible: 192.168.1.0/24, example.com, https://app.example.com..."
              className="w-full bg-brand-black-light border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-brand-green/60"
            />
            {workspaceId && (
              <p className="text-xs text-gray-500 flex items-center gap-1">
                <CheckCircle className="w-3 h-3 text-green-400" />
                Scans will be created in the current workspace
              </p>
            )}
            <button
              onClick={executeWorkflow}
              disabled={loading || !target.trim()}
              className="w-full py-2.5 bg-gradient-to-r from-indigo-600/80 to-purple-600/80 hover:from-indigo-600 hover:to-purple-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-sm font-semibold text-white flex items-center justify-center gap-2 transition-all"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Planification...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  Execute workflow
                </>
              )}
            </button>
          </div>
        )}

        {/* Execution result */}
        {executionResult && (
          <ExecutionResult
            result={executionResult}
            onClose={() => { setExecutionResult(null); setSelectedWorkflow(null); setSelectedWorkflowDetail(null); }}
          />
        )}
      </div>
    </div>
  );
};

export default PentestWorkflowSelector;
